import { useRef, useState } from 'react';
import {
  Card,
  Group,
  Title,
  Text,
  Badge,
  Button,
  Stack,
  Alert,
  Loader,
  ThemeIcon,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { AlertCircle, CheckCircle, RotateCcw, Trash2, UploadCloud } from 'react-feather';
import {
  sessionContentApi,
  type SessionContentFile,
  type SessionContentSlot,
} from '../../../../services/api/sessionContent';

/**
 * MED-784 / E1.3 — Polished upload-slot card for the Session Content Editor.
 *
 * Covers the full lifecycle:
 *   • Empty (AC12 partial)              — initial-upload affordance + helper text
 *   • Uploading                         — local file in flight, button shows spinner
 *   • Pending → Processing (AC11)       — server-side conversion in progress
 *   • Ready (AC5)                       — read-only display of filename + actions
 *   • Failed (AC11)                     — error message + Retry CTA + Replace CTA
 *   • Replace flow (AC6)                — confirm modal before re-uploading
 *
 * Server-side guarantees AC7 (one-file-per-slot) via the partial unique
 * index; this component just calls the upload endpoint, which handles the
 * soft-delete-replace transactionally.
 */
export interface SessionContentUploadSlotProps {
  cohortId: string;
  sessionId: string;
  slot: SessionContentSlot;
  file: SessionContentFile | null;
  required: boolean;
  label: string;
  helperText: string;
  acceptHint: string;
  icon: React.ReactNode;
  onChange: () => void;
  /**
   * MED-975 / AC-SDK2: optional extra warning rendered inside the
   * Replace/Remove confirmation modal. Used by the Session Agenda editor to
   * warn the CM that all section→slide associations will be cleared when
   * the slide deck is replaced or removed.
   */
  mutationConsequence?: React.ReactNode;
}

const STATE_LABEL: Record<string, { label: string; color: string }> = {
  pending: { label: 'Queued', color: 'gray' },
  processing: { label: 'Processing', color: 'warningAmber' },
  ready: { label: 'Ready', color: 'medleyGreen' },
  failed: { label: 'Failed', color: 'dangerRed' },
};

export function SessionContentUploadSlot({
  cohortId,
  sessionId,
  slot,
  file,
  required,
  label,
  helperText,
  acceptHint,
  icon,
  onChange,
  mutationConsequence,
}: SessionContentUploadSlotProps) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const upload = async (selected: File) => {
    setBusy(true);
    try {
      await sessionContentApi.upload(cohortId, sessionId, slot, selected);
      notifications.show({
        title: 'Upload accepted',
        message: `${selected.name} is being processed. We'll update this card when it's ready.`,
        color: 'medleyGreen',
      });
      onChange();
    } catch (err) {
      notifications.show({
        title: 'Upload failed',
        message: err instanceof Error ? err.message : 'Please try again.',
        color: 'dangerRed',
      });
    } finally {
      setBusy(false);
    }
  };

  const triggerPicker = () => inputRef.current?.click();

  const onPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!f) return;
    if (file) {
      // AC6 replace flow — confirm before clobbering the existing file.
      modals.openConfirmModal({
        title: `Replace ${label.toLowerCase()}?`,
        centered: true,
        children: (
          <Stack gap="xs">
            <Text size="sm">
              Replacing <strong>{file.original_filename}</strong> with{' '}
              <strong>{f.name}</strong>. The current file will be removed and the new
              file will be processed. This can&apos;t be undone.
            </Text>
            {mutationConsequence}
          </Stack>
        ),
        labels: { confirm: 'Replace', cancel: 'Cancel' },
        confirmProps: { color: 'medleyGreen' },
        onConfirm: () => void upload(f),
      });
      return;
    }
    await upload(f);
  };

  const handleRetry = async () => {
    setBusy(true);
    try {
      await sessionContentApi.retry(cohortId, sessionId, slot);
      notifications.show({
        title: 'Retry queued',
        message: 'The conversion has been re-enqueued.',
        color: 'medleyGreen',
      });
      onChange();
    } catch (err) {
      notifications.show({
        title: 'Retry failed',
        message: err instanceof Error ? err.message : 'Please try again.',
        color: 'dangerRed',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = () => {
    if (!file) return;
    modals.openConfirmModal({
      title: `Remove ${label.toLowerCase()}?`,
      centered: true,
      children: (
        <Stack gap="xs">
          <Text size="sm">
            Remove <strong>{file.original_filename}</strong>?
            {required && (
              <>
                {' '}
                This slot is required before the session can go live; you&apos;ll need to
                upload a replacement before the session date.
              </>
            )}
          </Text>
          {mutationConsequence}
        </Stack>
      ),
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'dangerRed' },
      onConfirm: async () => {
        setBusy(true);
        try {
          await sessionContentApi.removeSlot(cohortId, sessionId, slot);
          notifications.show({
            title: `${label} removed`,
            message: 'You can upload a new file at any time.',
            color: 'medleyGreen',
          });
          onChange();
        } catch (err) {
          notifications.show({
            title: 'Could not remove file',
            message: err instanceof Error ? err.message : 'Please try again.',
            color: 'dangerRed',
          });
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const stateInfo = file ? STATE_LABEL[file.processing_state] : null;

  return (
    <Card withBorder padding="lg" radius="md">
      <input
        ref={inputRef}
        type="file"
        hidden
        onChange={(e) => void onPicked(e)}
        accept=".pdf,.docx,.pptx,.doc,.ppt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation"
      />

      <Group justify="space-between" align="flex-start" mb="xs">
        <Group align="center" gap="sm">
          <ThemeIcon variant="light" color="gray" size="lg" radius="md">
            {icon}
          </ThemeIcon>
          <Stack gap={2}>
            <Title order={4}>{label}</Title>
            <Group gap="xs">
              {required ? (
                <Badge color="dangerRed" variant="light" radius="sm" tt="uppercase" fz={10}>
                  Required
                </Badge>
              ) : (
                <Badge color="gray" variant="light" radius="sm" tt="uppercase" fz={10}>
                  Optional
                </Badge>
              )}
              {stateInfo && (
                <Badge color={stateInfo.color} variant="light" radius="sm" tt="uppercase" fz={10}>
                  {stateInfo.label}
                </Badge>
              )}
            </Group>
          </Stack>
        </Group>
      </Group>

      <Text size="sm" c="dimmed" mb="md">
        {helperText}
      </Text>

      {!file && (
        <Stack gap="xs">
          <Button
            leftSection={<UploadCloud size={16} />}
            onClick={triggerPicker}
            loading={busy}
            color="medleyGreen"
          >
            Upload {label.toLowerCase()}
          </Button>
          <Text size="xs" c="dimmed">
            {acceptHint}
          </Text>
        </Stack>
      )}

      {file && (
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <Text fw={600}>{file.original_filename}</Text>
              {file.processing_state === 'ready' && (
                <ThemeIcon size="sm" variant="light" color="medleyGreen" radius="xl">
                  <CheckCircle size={12} />
                </ThemeIcon>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              Uploaded {formatRelative(file.uploaded_at)}
            </Text>
          </Group>

          {(file.processing_state === 'pending' || file.processing_state === 'processing') && (
            <Group gap="xs">
              <Loader size="xs" color="warningAmber" />
              <Text size="sm" c="dimmed">
                {file.processing_state === 'pending'
                  ? 'Queued for conversion…'
                  : 'Converting and parsing sections…'}
              </Text>
            </Group>
          )}

          {file.processing_state === 'failed' && (
            <Alert
              color="dangerRed"
              variant="light"
              icon={<AlertCircle size={16} />}
              title="Conversion failed"
            >
              <Text size="sm">{file.error_message ?? 'Conversion failed.'}</Text>
            </Alert>
          )}

          <Group gap="xs">
            <Button
              size="sm"
              variant="default"
              leftSection={<UploadCloud size={14} />}
              onClick={triggerPicker}
              loading={busy}
            >
              Replace…
            </Button>
            {file.processing_state === 'failed' && (
              <Button
                size="sm"
                color="warningAmber"
                leftSection={<RotateCcw size={14} />}
                onClick={() => void handleRetry()}
                loading={busy}
              >
                Retry conversion
              </Button>
            )}
            <Button
              size="sm"
              variant="subtle"
              color="dangerRed"
              leftSection={<Trash2 size={14} />}
              onClick={handleRemove}
              loading={busy}
            >
              Remove
            </Button>
          </Group>
        </Stack>
      )}
    </Card>
  );
}

/**
 * Lightweight relative time. Avoids pulling in date-fns just for this. Goes
 * "just now → Xm ago → Xh ago → on Mon, Jan 1" without a runtime dependency.
 */
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const elapsedSec = Math.max(0, (Date.now() - then) / 1000);
  if (elapsedSec < 60) return 'just now';
  if (elapsedSec < 3600) return `${Math.floor(elapsedSec / 60)}m ago`;
  if (elapsedSec < 86_400) return `${Math.floor(elapsedSec / 3600)}h ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}
